import { api } from "../../api/axios";
import { apiUrl } from "../../config/backendFeatures";
import { useAuthStore } from "../../store/useAuthStore";
import { EMPTY_ANSWER, mergeAnswer, normalizeListings, normalizeSuggestions, CARDS_SHOWN } from "./normalize";
import type { AiAnswer, AiMessage, AiSuggestion } from "./types";

// Turaq AI — backend docs/AI_OFFICIAL_API.md (Б).

const GUEST_KEY = "turaq-ai-guest-id";

/** Guests are told apart by X-Guest-Id; the server hands one out on the first answer. */
export function guestId(): string | null {
  try {
    return localStorage.getItem(GUEST_KEY);
  } catch {
    return null;
  }
}

function rememberGuest(id: unknown) {
  if (typeof id !== "string" || !id) return;
  try {
    localStorage.setItem(GUEST_KEY, id);
  } catch {
    /* private mode */
  }
}

function headers(json = true): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  const guest = guestId();
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : guest ? { "X-Guest-Id": guest } : {}),
  };
}

/** Server expects ky|ru; the site's Kyrgyz is "kg". */
export const aiLang = (lang: string) => {
  const l = (lang || "kg").split("-")[0];
  return l === "ru" ? "ru" : "ky";
};

export async function fetchSuggestions(lang: string): Promise<AiSuggestion[]> {
  const { data } = await api.get(apiUrl("/api/ai/suggestions"), { params: { lang: aiLang(lang) } });
  return normalizeSuggestions(data);
}

export class AiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status?: number,
    readonly retryAfter?: number,
  ) {
    super(message);
  }
}

async function errorOf(res: Response): Promise<AiError> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* not JSON */
  }
  const code = body?.code ?? (res.status === 429 ? "AI_RATE_LIMITED" : res.status === 400 ? "AI_VALIDATION" : "AI_INTERNAL");
  return new AiError(code, String(body?.message ?? res.statusText), res.status, Number(body?.retryAfter) || undefined);
}

export interface AskInput {
  message: string;
  conversationId: string | null;
  signal?: AbortSignal;
  /** «Издеп жатам…» → cards → the text, piece by piece. */
  onUpdate: (answer: AiAnswer, stage: "searching" | "streaming") => void;
}

export interface AskResult {
  answer: AiAnswer;
  conversationId: string | null;
}

/**
 * POST /api/ai/search/stream (SSE: status → result → delta* → done | error).
 * If the stream endpoint is missing or the proxy buffers it into JSON, the
 * same body goes to POST /api/ai/search.
 */
export async function askAssistant({ message, conversationId, signal, onUpdate }: AskInput): Promise<AskResult> {
  const body = JSON.stringify({ message, ...(conversationId ? { conversation_id: conversationId } : {}) });
  let res = await fetch(apiUrl("/api/ai/search/stream"), {
    method: "POST",
    signal,
    headers: { ...headers(), Accept: "text/event-stream" },
    body,
  });
  if (res.status === 404 || res.status === 405) {
    res = await fetch(apiUrl("/api/ai/search"), { method: "POST", signal, headers: headers(), body });
  }
  if (!res.ok) throw await errorOf(res);

  let answer = EMPTY_ANSWER;
  let convId = conversationId;
  const absorb = (d: any) => {
    if (!d || typeof d !== "object") return;
    convId = d.conversation_id ?? convId;
    rememberGuest(d.guest_id);
  };

  if (!(res.headers.get("content-type") || "").includes("text/event-stream") || !res.body) {
    const data = await res.json();
    absorb(data);
    answer = mergeAnswer(answer, data);
    onUpdate(answer, "streaming");
    return { answer, conversationId: convId };
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  let failure: AiError | null = null;

  const handle = (event: string, raw: string) => {
    let data: any = raw;
    try {
      data = JSON.parse(raw);
    } catch {
      /* plain text */
    }
    absorb(data);
    switch (event) {
      case "status":
        onUpdate(answer, "searching");
        return;
      case "result":
        // Cards first, text follows in deltas.
        answer = mergeAnswer(answer, { ...data, reply_text: undefined, text: undefined });
        break;
      case "delta":
        answer = mergeAnswer(answer, typeof data === "string" ? data : { text: data?.text ?? "" }, "append");
        break;
      case "done":
        answer = mergeAnswer(answer, data);
        break;
      case "error":
        failure = new AiError(String(data?.code ?? "AI_INTERNAL"), String(data?.message ?? "error"));
        return;
      default:
        answer = mergeAnswer(answer, data);
    }
    onUpdate(answer, "streaming");
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let cut: number;
    while ((cut = buffer.search(/\r?\n\r?\n/)) >= 0) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut).replace(/^\r?\n\r?\n/, "");
      let event = "message";
      const lines: string[] = [];
      for (const line of block.split(/\r?\n/)) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) lines.push(line.slice(5).replace(/^ /, ""));
      }
      if (lines.length > 0) handle(event, lines.join("\n"));
    }
  }
  if (failure) throw failure;
  return { answer, conversationId: convId };
}

/** Earlier turns of a conversation (the server keeps the last 50). */
export async function fetchAiHistory(conversationId: string): Promise<AiMessage[]> {
  const res = await fetch(apiUrl(`/api/ai/conversations/${encodeURIComponent(conversationId)}/messages`), { headers: headers(false) });
  if (!res.ok) throw await errorOf(res);
  const data = await res.json();
  const items: any[] = Array.isArray(data?.items) ? data.items : [];
  return items.map((m): AiMessage => {
    const createdAt = String(m.created_at ?? new Date().toISOString());
    if (m.role === "user") return { id: String(m.id), role: "user", text: String(m.text ?? ""), createdAt };
    return {
      id: String(m.id),
      role: "assistant",
      createdAt,
      status: "done",
      replyTo: "",
      answer: {
        ...EMPTY_ANSWER,
        text: String(m.text ?? ""),
        listings: normalizeListings(m.listings).slice(0, CARDS_SHOWN),
        total: Number(m.total_count ?? 0),
        moreUrl: m.more_url ?? null,
        filters: m.filters ?? null,
        suggestSaveSearch: Number(m.total_count ?? 0) === 0 && Boolean(m.filters),
      },
    };
  });
}
